import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDesignSystem } from '../../context/DesignSystemContext';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { loadLottiePlayer } from 'utils/lottiePlayer';
import loaderSpinnerAnimation from 'assets/images/loader-spinner.json';
import successCheckAnimation from 'assets/images/success-check.json';
import errorFailAnimation from 'assets/images/error-fail.json';
import { asyncStateViewI18n } from './i18n';
import './asyncStateView.scss';

// Config del Lottie por estado (mismo ciclo de vida/caja 96×96 que el loader):
// loading gira en loop; success corre 1× a 1.5x y al TERMINAR dispara el auto-cierre;
// error corre 1× una vez y queda en el último frame (sin auto-cierre); warning reusa el
// check de éxito (la acción SÍ se completó) y tampoco auto-cierra.
const LOTTIE_BY_STATUS = {
  loading: { data: loaderSpinnerAnimation, loop: true, speed: 1 },
  success: { data: successCheckAnimation, loop: false, speed: 1.5 },
  warning: { data: successCheckAnimation, loop: false, speed: 1.5 },
  error: { data: errorFailAnimation, loop: false, speed: 1 },
};

/**
 * AsyncStateView
 *
 * Generaliza el body-swap de SacTranscriptsFilterModal: envuelve el cuerpo de un modal y,
 * según `status`, muestra el formulario (idle) o reemplaza todo por un estado de carga /
 * éxito / error.
 *
 * - idle    → renderiza `children` sin tocarlos (el formulario y sus botones actuales).
 * - loading → loader Lottie (loader-spinner.json vía el util compartido; fallback
 *             spinner-border si el player no carga) + título/descripción. Sin botones.
 * - success → check Lottie (success-check.json, misma caja 96×96 del loader) + título/
 *             descripción; al TERMINAR la animación (evento `complete`) llama
 *             `onSuccessAutoClose` una sola vez. Fallback: si el player no cargó, rige el
 *             timer `successDelayMs`.
 * - warning → la acción SÍ se completó pero con un efecto secundario fallido (ej. no se
 *             pudo notificar al cliente): MISMO check de éxito + `warningTitle`/
 *             `warningDescription`, SIN auto-cierre y con un único botón "Entendido"
 *             (`onBack`) para que el usuario lo cierre a mano.
 * - error   → X Lottie (error-fail.json), queda en el último frame + título + mensaje (del
 *             catch) + un único botón "Volver" (`onBack`); sin auto-cierre.
 *
 * DS por contexto (mismo patrón que TaskConfirmation); si el contexto es null cae a un
 * spinner-border / <button> nativos para no romper.
 */
const AsyncStateView = ({
  status = 'idle',
  loadingTitle,
  loadingDescription,
  successTitle,
  successDescription,
  warningTitle,
  warningDescription,
  errorTitle,
  errorMessage,
  backLabel,
  onBack,
  onSuccessAutoClose,
  successDelayMs = 1200,
  children,
}) => {
  const { t } = useScopedTranslation(asyncStateViewI18n);
  const { Button, SvgIcon } = useDesignSystem?.() || {};

  const lottieRef = useRef(null);
  const [hasLottie, setHasLottie] = useState(false);
  // ¿El player Lottie cargó de verdad? (ref sincrónico: decide si el auto-cierre de
  // éxito lo dispara la animación o el timer de fallback, sin depender del re-render).
  const lottieLoadedRef = useRef(false);
  // Guarda: `onSuccessAutoClose` se llama UNA sola vez por entrada a `success`.
  const autoCloseFiredRef = useRef(false);

  // Mantiene la última referencia del auto-cierre sin re-armar el timer en cada render
  // (evita que un onSuccessAutoClose inline reinicie la cuenta).
  const autoCloseRef = useRef(onSuccessAutoClose);
  useEffect(() => {
    autoCloseRef.current = onSuccessAutoClose;
  });

  // Ciclo de vida del Lottie: carga la animación del estado actual (loading/success/error)
  // y hace `destroy()` al salir del estado o desmontar (mismo patrón que el loader).
  useEffect(() => {
    const config = LOTTIE_BY_STATUS[status];
    if (!config) return undefined;

    let animation = null;
    let cancelled = false;
    const el = lottieRef.current;
    setHasLottie(false);
    lottieLoadedRef.current = false;
    if (status === 'success') autoCloseFiredRef.current = false;

    loadLottiePlayer()
      .then((lottie) => {
        if (cancelled || !lottie || !el) return;
        animation = lottie.loadAnimation({
          container: el,
          renderer: 'svg',
          loop: config.loop,
          autoplay: true,
          animationData: config.data,
        });
        animation.setSpeed(config.speed);
        // Éxito: al TERMINAR la animación dispara el auto-cierre (una sola vez).
        if (status === 'success') {
          animation.addEventListener('complete', () => {
            if (autoCloseFiredRef.current) return;
            autoCloseFiredRef.current = true;
            autoCloseRef.current?.();
          });
        }
        lottieLoadedRef.current = true;
        setHasLottie(true);
      })
      .catch(() => {
        lottieLoadedRef.current = false;
        setHasLottie(false);
      });

    return () => {
      cancelled = true;
      if (animation) animation.destroy();
    };
  }, [status]);

  // Fallback del auto-cierre de éxito: SOLO si el player Lottie no cargó (sin animación no
  // llega el evento `complete`) → rige el timer `successDelayMs`. Con player, el cierre lo
  // dispara el `complete` de la animación (~1.45 s a 1.5x).
  useEffect(() => {
    if (status !== 'success') return undefined;
    const timer = setTimeout(() => {
      if (lottieLoadedRef.current || autoCloseFiredRef.current) return;
      autoCloseFiredRef.current = true;
      autoCloseRef.current?.();
    }, successDelayMs);
    return () => clearTimeout(timer);
  }, [status, successDelayMs]);

  if (status === 'loading') {
    return (
      <div
        className='async-state-view async-state-view--fade d-flex flex-column align-items-center justify-content-center text-center'
        role='status'
        aria-live='polite'
      >
        <div
          ref={lottieRef}
          className='async-state-view__lottie'
          aria-hidden='true'
        />
        {!hasLottie && (
          <div className='spinner-border text-primary' aria-hidden='true' />
        )}
        <h4 className='fw-semibold mb-1 mt-3'>
          {loadingTitle || t('asv_loading_title')}
        </h4>
        <p className='font-light caption mb-0'>
          {loadingDescription || t('asv_loading_desc')}
        </p>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className='async-state-view async-state-view--fade d-flex flex-column align-items-center justify-content-center text-center'>
        <div
          ref={lottieRef}
          className='async-state-view__lottie'
          aria-hidden='true'
        />
        {!hasLottie &&
          (SvgIcon ? (
            <SvgIcon name='circleCheck' stroke='var(--success-500)' />
          ) : (
            <div
              className='async-state-view__glyph async-state-view__glyph--success'
              aria-hidden='true'
            >
              ✓
            </div>
          ))}
        <h4 className='fw-semibold mb-1 mt-3'>
          {successTitle || t('asv_success_title')}
        </h4>
        <p className='font-light caption mb-0'>
          {successDescription || t('asv_success_desc')}
        </p>
      </div>
    );
  }

  if (status === 'warning') {
    return (
      <div className='async-state-view async-state-view--fade d-flex flex-column align-items-center justify-content-center text-center'>
        <div
          ref={lottieRef}
          className='async-state-view__lottie'
          aria-hidden='true'
        />
        {!hasLottie &&
          (SvgIcon ? (
            <SvgIcon name='circleCheck' stroke='var(--success-500)' />
          ) : (
            <div
              className='async-state-view__glyph async-state-view__glyph--success'
              aria-hidden='true'
            >
              ✓
            </div>
          ))}
        <h4 className='fw-semibold mb-1 mt-3'>
          {warningTitle || t('asv_warning_title')}
        </h4>
        <p className='font-light caption mb-3'>
          {warningDescription || t('asv_warning_desc')}
        </p>
        {Button ? (
          <Button
            label={backLabel || t('asv_got_it')}
            variant='secondary'
            size='sm'
            onClick={onBack}
          />
        ) : (
          <button
            type='button'
            className='btn btn-secondary btn-sm'
            onClick={onBack}
          >
            {backLabel || t('asv_got_it')}
          </button>
        )}
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className='async-state-view async-state-view--fade d-flex flex-column align-items-center justify-content-center text-center'>
        <div
          ref={lottieRef}
          className='async-state-view__lottie'
          aria-hidden='true'
        />
        {!hasLottie &&
          (SvgIcon ? (
            <SvgIcon name='circleX' stroke='var(--error-500)' />
          ) : (
            <div
              className='async-state-view__glyph async-state-view__glyph--error'
              aria-hidden='true'
            >
              !
            </div>
          ))}
        <h4 className='fw-semibold mb-1 mt-3'>
          {errorTitle || t('asv_error_title')}
        </h4>
        <p className='font-light caption mb-3'>
          {errorMessage || t('asv_error_desc')}
        </p>
        {Button ? (
          <Button
            label={backLabel || t('asv_back')}
            variant='secondary'
            size='sm'
            onClick={onBack}
          />
        ) : (
          <button
            type='button'
            className='btn btn-secondary btn-sm'
            onClick={onBack}
          >
            {backLabel || t('asv_back')}
          </button>
        )}
      </div>
    );
  }

  // idle (y cualquier status desconocido): el formulario tal cual.
  return <>{children}</>;
};

AsyncStateView.propTypes = {
  status: PropTypes.oneOf(['idle', 'loading', 'success', 'warning', 'error']),
  loadingTitle: PropTypes.string,
  loadingDescription: PropTypes.string,
  successTitle: PropTypes.string,
  successDescription: PropTypes.string,
  warningTitle: PropTypes.string,
  warningDescription: PropTypes.string,
  errorTitle: PropTypes.string,
  errorMessage: PropTypes.string,
  backLabel: PropTypes.string,
  onBack: PropTypes.func,
  onSuccessAutoClose: PropTypes.func,
  successDelayMs: PropTypes.number,
  children: PropTypes.node,
};

export default AsyncStateView;
